import { Alert, Box, Flex, Heading, IconButton, useToast, VStack } from '@chakra-ui/react';
import { Icon } from '@common/components/Icon';
import { Image } from '@common/components/Image';
import sharing from '@common/sharing';
import { FontFamily } from '@common/theming/Fonts';
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TextBlock } from '../../common/components/TextBlock';
import { ROUTES } from '../../config';
import { Loading } from '../../property/views/common/Loading';
import { useWaitlistViewModel } from '../models/WaitlistViewModel';
import { Footer } from './components/Footer';
import { Section } from './components/Section';

export const WaitListStatusPage: React.FC = () => {
  const model = useWaitlistViewModel();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState<'CHECKING' | 'SHOWING'>('CHECKING');
  const [errorMessage, setErrorMessage] = useState<string>();
  const shareLink = `https://invest.concreit.com/waitlist/?referralCode=${model.code}`;
  const toast = useToast();

  async function checkStatus(code: string) {
    setState('CHECKING');
    const result = await model.checkStatusByCode(code);
    if (result.error) setErrorMessage(result.error);
    setState('SHOWING');
  }

  function copyLinkToClipboard() {
    navigator.clipboard.writeText(shareLink);
    toast({
      description: 'Share this link with your friends to get them on the waitlist!',
      duration: 2000,
      position: 'top-right',
      status: 'info',
      title: 'Link Copied',
      variant: 'solid',
    });
  }

  useEffect(() => {
    const code = params.get('code');
    const position = params.get('position');
    if (code) model.setCode(code);
    if (position) model.setPosition(Number(position));
    if (!code) return navigate(ROUTES.waitlist);
    if (code && position) return setState('SHOWING');
    checkStatus(code);
  }, []);

  let content = null;

  switch (state) {
    case 'CHECKING':
      content = (
        <Box>
          <Loading />
        </Box>
      );
      break;
    case 'SHOWING':
      content = (
        <>
          {/* Header */}
          <Box px={[10, 20]}>
            <Heading
              textAlign={'center'}
              fontFamily={FontFamily.DM_SERIF_DISPLAY_REGULAR}
              fontSize={['3xl', '6xl']}
              fontWeight={'400'}>
              You're in line!
            </Heading>
          </Box>
          {/* Subtitle */}
          <Box px={[10, 20]}>
            <TextBlock mb={'30px'} textAlign={'center'} color="gray.900" fontSize={'20px'} lineHeight={'120%'}>
              Welcome to our priority access list. Stay tuned for announcements.
            </TextBlock>
          </Box>

          {/* Error */}
          {errorMessage && (
            <Box>
              <Alert status="error">
                <TextBlock>{errorMessage}</TextBlock>
              </Alert>
            </Box>
          )}

          {/* Position */}
          <Box
            p="2px"
            display="inline-block"
            background={'linear-gradient(to bottom, #63AAFF, #C7E0FF, #FFD698, #C4A8FF)'}
            borderRadius={'8px'}
            borderColor={'white'}>
            <Box background={'white'} p={'20px'} borderRadius={'6px'}>
              <Heading
                textAlign={'center'}
                fontFamily={FontFamily.HELVETICA_NOW_BOLD}
                fontWeight={'normal'}
                fontSize={'58px'}>
                {model.position}
              </Heading>
              <TextBlock textAlign={'center'} weight="bold">
                investors ahead of you
              </TextBlock>
            </Box>
          </Box>

          {/* Referral Link(s) */}
          <Box>
            <TextBlock
              mt={'30px'}
              textAlign={'center'}
              color="gray.900"
              weight="bold"
              fontSize={'20px'}
              lineHeight={'120%'}>
              Want to cut the line? Refer your friends.
            </TextBlock>
            <Box
              p="2px"
              display="inline-block"
              background={'linear-gradient(180deg, #0B7AFF 0%, rgba(48, 142, 255, 0.26) 100%);'}
              borderRadius={'8px'}
              borderColor={'white'}
              mt={'30px'}>
              <Box
                bgColor={'white'}
                cursor="pointer"
                borderRadius={'6px'}
                fontWeight="bold"
                paddingX={'30px'}
                onClick={copyLinkToClipboard}
                paddingY="20px">
                <TextBlock textAlign={'center'}>{shareLink}</TextBlock>
              </Box>
            </Box>
          </Box>
        </>
      );
      break;
  }

  return (
    <VStack bg="black" minH={'100vh'} px={'40px'}>
      {/* Header */}
      <Flex width="full" direction="row" height={[70, 90]} color="white" alignItems={'center'} zIndex={'3'}>
        <Image image="logo-white" w={[100, 160]} margin={'auto auto'} />
      </Flex>

      {/* Graphic */}
      <Box maxW={[730]} px={['30px', '100px']} width={'100%'} height={'100%'} zIndex={'2'} position={'relative'}>
        <Box
          px={['30px', '100px']}
          position={'absolute'}
          background={'linear-gradient(360deg, rgba(0,0,0,0) 0%, rgba(0,0,0,1) 100%)'}
          maxWidth={[670, 530]}
          width={'100%'}
          height={'100%'}
          zIndex={'2'}
          mt={'-50px'}
          mb={'-100px'}></Box>
        <Image image="dashboard-header" mt={'-50px'} mb={'-100px'} zIndex={'1'} position={'relative'} />
      </Box>

      {/* Body */}
      <VStack
        bgColor={'white'}
        borderRadius={'10px'}
        maxW={[730]}
        padding={['10px 10px 30px', '20px 20px 60px']}
        zIndex={'2'}>
        <Alert
          borderRadius={'8px'}
          fontFamily={FontFamily.HELVETICA_NOW_BOLD}
          fontWeight={'normal'}
          status="success"
          mb={'30px'}>
          You have successfully joined the priority access list.
        </Alert>

        {/* Early Access Faux-Button */}
        <Box
          p="2px"
          display="inline-block"
          background={'linear-gradient(to bottom, #63AAFF, #C7E0FF, #FFD698, #C4A8FF)'}
          borderRadius={'8px'}
          borderColor={'white'}>
          <Box bgColor={'white'} borderRadius="6px" px="20px">
            <TextBlock fontWeight={'bold'} fontSize={'12px'} lineHeight="30px">
              Get Early Access!
            </TextBlock>
          </Box>
        </Box>
        {/* Content */}
        {content}
        {/* Social Medial Share Buttons */}
        <Section title="Sharing" id="sharing">
          <div
            style={{
              color: 'white',
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              gap: 40,
              height: '100%',
            }}>
            <IconButton
              aria-label="Share on Facebook"
              onClick={() =>
                sharing.toFacebook({
                  quote: `Sign up for exclusive priority access to buy shares of Concreit's single-family rentals with my referral code: ${model.code}`,
                  url: `${shareLink}`,
                })
              }
              position="relative"
              variant="unstyled"
              size="sm"
              color={'muted'}
              zIndex="skipLink"
              icon={
                <Icon icon="facebook" boxSize={['30px', '40px']} color={'gray.900'} _hover={{ color: 'blue.500' }} />
              }
            />
            <IconButton
              aria-label="Share on LinkedIn"
              onClick={() =>
                sharing.toLinkedIn({
                  summary: `Use my code ${model.code} for a chance to get first pick of shares for Concreit's rental homes`,
                  title: 'Get priority access to single-family rentals',
                  url: `${shareLink}`,
                })
              }
              position="relative"
              variant="unstyled"
              size="sm"
              color={'muted'}
              zIndex="skipLink"
              icon={
                <Icon icon="linkedIn" boxSize={['30px', '40px']} color={'gray.900'} _hover={{ color: 'blue.500' }} />
              }
            />
            <IconButton
              aria-label="Share on Twitter"
              onClick={() =>
                sharing.toTwitter({
                  tweet: `Sign up for exclusive priority access to buy shares of Concreit's single-family rentals with my referral code: ${model.code}`,
                  url: `${shareLink}`,
                })
              }
              position="relative"
              variant="unstyled"
              size="sm"
              color={'muted'}
              zIndex="skipLink"
              icon={
                <Icon icon="twitter" boxSize={['30px', '40px']} color={'gray.900'} _hover={{ color: 'blue.500' }} />
              }
            />
          </div>
        </Section>
      </VStack>
      {/* Footer */}
      <Footer />
    </VStack>
  );
};
